import { useCallback, useRef } from 'react';
import { Linking } from 'react-native';

import { showErrorAlert } from '@/components/ui';
import { requestShopLinkUrl } from '@/features/shop/services/shop-link';
import { captureException } from '@/libs/logger';
import { globalLoading } from '@/stores';

/**
 * Opens the Shopify store already logged in as the current account. Requests a one-time
 * `/shop-bridge` URL from the IdP and hands it to the system browser (Safari/Chrome), which signs
 * the fresh web session in silently and lands on the store.
 *
 * Meant to run straight off the Shop tab press (via a `tabPress` listener that `preventDefault`s) —
 * no Shop screen is rendered. Guards double taps, shows the global loader while minting the link,
 * and surfaces failures with an alert.
 */
export function useOpenShop() {
  const inFlight = useRef(false);

  return useCallback(async (redirectUrl: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    globalLoading.show();
    try {
      const url = await requestShopLinkUrl(redirectUrl);
      await Linking.openURL(url);
    } catch (error) {
      captureException(
        { error_payload: error },
        { tags: { usk_log_type: 'shop_link_open_error' } },
      );
      showErrorAlert("Couldn't open the shop. Please try again.");
    } finally {
      globalLoading.hide();
      inFlight.current = false;
    }
  }, []);
}
